import { apiClient } from '@/lib/api-client';
import type {
  RfmScore,
  IdentityScore,
  LoyaltyTransaction,
} from '@bbpos/types';

// ─── Request / Response types ────────────────────────────────────────────────

export interface CustomerListItem {
  id: string;
  name: string;
  phone: string;
  email: string | null;
  loyalty_points: number;
  /** Unused, unexpired store-credit balance in paise. */
  store_credit_balance: number;
  total_visits: number;
  total_spent: number;
  tags: string[];
  last_visit_at: string | null;
  created_at: string;
}

export interface CustomerDetail {
  id: string;
  tenant_id: string;
  name: string;
  phone: string;
  email: string | null;
  date_of_birth: string | null;
  anniversary: string | null;
  address: string | null;
  notes: string | null;
  tags: string[];
  loyalty_points: number;
  /** CU2-009: the nearest points to expire (YYYY-MM-DD), or null. */
  expiring_points?: { amount: number; expiry_date: string } | null;
  /** Unused, unexpired store-credit balance in paise. */
  store_credit_balance: number;
  /** GET /customers/:id names these visit_count / avg_order_value. */
  visit_count: number;
  total_spent: number;
  avg_order_value: number;
  last_visit_at: string | null;
  recent_bills: {
    id: string;
    bill_number: string;
    total_amount: number;
    created_at: string;
  }[];
  created_at: string;
  updated_at: string;
  /** Bumped on every edit; an offline edit sends the version it started from. */
  version?: number;
  /** CU4-003: marketing opt-outs per channel (absent / false = opted in). */
  email_opt_out?: boolean;
  whatsapp_opt_out?: boolean;
  sms_opt_out?: boolean;
}

export interface CustomerLookupResult {
  id: string;
  name: string;
  phone: string;
  email: string | null;
  loyalty_points: number;
  /** Unused, unexpired store-credit balance in paise. */
  store_credit_balance: number;
  total_visits: number;
  total_spent: number;
  tags: string[];
  /** CU4-003: marketing opt-outs per channel (absent / false = opted in). */
  email_opt_out?: boolean;
  whatsapp_opt_out?: boolean;
  sms_opt_out?: boolean;
}

/**
 * The customer's currently active membership, as returned by
 * GET /customers/:id/active-membership. Null when the customer has no active
 * enrollment. `discount_percentage` (0..100) is the tier discount the POS
 * auto-applies to the bill.
 */
export interface CustomerActiveMembership {
  id: string;
  customer_id: string;
  membership_plan_id: string;
  plan_name: string;
  tier_level: string;
  discount_percentage: number;
  fee_paid: number;
  enrolled_at: string;
  expires_at: string;
  status: string;
  auto_renewal: boolean;
}

interface CreateCustomerDto {
  name: string;
  phone: string;
  email?: string;
  date_of_birth?: string;
  anniversary?: string;
  address?: string;
  notes?: string;
  tags?: string[];
  /** CU4-003: the customer asked for no marketing messages (all channels). */
  marketing_opt_out?: boolean;
}

/** CU4-003: PATCH /customers/:id/consent — an omitted channel is left as it is. */
export interface CustomerConsent {
  email_opt_out?: boolean;
  whatsapp_opt_out?: boolean;
  sms_opt_out?: boolean;
}

interface CreateCustomerResponse {
  id: string;
  tenant_id: string;
  name: string;
  phone: string;
  email: string | null;
  date_of_birth: string | null;
  anniversary: string | null;
  address: string | null;
  notes: string | null;
  tags: string[];
  loyalty_points: number;
  /** Unused, unexpired store-credit balance in paise. */
  store_credit_balance: number;
  total_visits: number;
  total_spent: number;
  created_at: string;
}

interface UpdateCustomerDto {
  name?: string;
  email?: string;
  date_of_birth?: string;
  anniversary?: string;
  address?: string;
  notes?: string;
  tags?: string[];
}

interface UpdateCustomerResponse {
  id: string;
  name: string;
  email: string | null;
  tags: string[];
  updated_at: string;
}

interface CustomerListFilters {
  search?: string;
  tag?: string;
  /** Only active (not deleted) customers — the till's attach searches. */
  active?: boolean;
  sort?: string;
  page?: number;
  limit?: number;
}

interface LoyaltyHistoryFilters {
  start_date?: string;
  end_date?: string;
  page?: number;
  limit?: number;
}

interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

// ─── Service ─────────────────────────────────────────────────────────────────

/** `GET /loyalty/customers/:id/balance` — balance and the tenant's rules. */
export interface LoyaltyBalance {
  customer_id: string;
  customer_name: string;
  loyalty_points: number;
  /** What the whole balance is worth, in minor units. */
  value_paise: number;
  program_active: boolean;
  min_redemption_points: number;
  /** Minor units per point. Multiply directly — the product is minor units. */
  redemption_value_paise: number;
}

export const customersService = {
  list(filters: CustomerListFilters = {}) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== null) {
        params.set(key, String(value));
      }
    }
    const qs = params.toString();
    return apiClient<{ data: CustomerListItem[]; meta: PaginationMeta }>(
      `/customers${qs ? `?${qs}` : ''}`,
    );
  },

  lookupByPhone(phone: string) {
    return apiClient<CustomerLookupResult>(
      `/customers/lookup/${encodeURIComponent(phone)}`,
    );
  },

  getById(customerId: string) {
    return apiClient<CustomerDetail>(`/customers/${customerId}`);
  },

  /**
   * The customer's currently active membership (or null). The backend already
   * auto-applies this tier discount when a bill is created; the POS fetches it
   * so the on-screen total and the membership banner match the stored bill.
   */
  getActiveMembership(customerId: string) {
    return apiClient<CustomerActiveMembership | null>(
      `/customers/${customerId}/active-membership`,
    );
  },

  getRfm(customerId: string) {
    return apiClient<RfmScore>(`/customers/${customerId}/rfm`);
  },

  getIdentityScore(customerId: string) {
    return apiClient<IdentityScore>(`/customers/${customerId}/identity-score`);
  },

  getLoyaltyHistory(customerId: string, filters: LoyaltyHistoryFilters = {}) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== null) {
        params.set(key, String(value));
      }
    }
    const qs = params.toString();
    return apiClient<{ data: LoyaltyTransaction[]; meta: PaginationMeta }>(
      `/customers/${customerId}/loyalty/history${qs ? `?${qs}` : ''}`,
    );
  },

  /**
   * Balance plus the tenant's redemption rules, for the payment screen.
   *
   * `GET /loyalty/customers/:id/balance` has existed since the loyalty module
   * shipped — `@Roles('CASHIER')`, its own summary says "used by POS checkout"
   * — and had no caller, which is why the till could only ever redeem points
   * all-or-nothing through the WALLET tender.
   */
  getLoyaltyBalance(customerId: string) {
    return apiClient<LoyaltyBalance>(`/loyalty/customers/${customerId}/balance`);
  },

  create(dto: CreateCustomerDto) {
    return apiClient<CreateCustomerResponse>('/customers', {
      method: 'POST',
      body: dto,
    });
  },

  /** CU4-003: the customer asks at the till to stop (or restart) marketing. */
  setConsent(customerId: string, consent: CustomerConsent) {
    return apiClient<CustomerConsent>(`/customers/${customerId}/consent`, {
      method: 'PATCH',
      body: { ...consent, source: 'POS' },
    });
  },

  update(customerId: string, dto: UpdateCustomerDto) {
    return apiClient<UpdateCustomerResponse>(`/customers/${customerId}`, {
      method: 'PATCH',
      body: dto,
    });
  },
};
